import React, { useState, useEffect } from 'react';
import { Card, Badge, ProgressBar, Spinner, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faTasks,
  faRocket,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faBrain,
  faRobot,
  faHome,
  faFileContract,
  faCalendarCheck,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface DealWorkflowStage {
  stage_id: string;
  stage_name: string;
  stage_order: number;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  completion_percentage: number;
  tasks_completed: number;
  total_tasks: number;
  estimated_completion: string;
  is_current: boolean;
  alerts: DealWorkflowAlert[];
}

interface DealWorkflowAlert {
  alert_id: string;
  alert_type: 'overdue' | 'due_soon' | 'blocked' | 'attention_needed';
  priority: 'critical' | 'high' | 'medium' | 'low';
  message: string;
  stage_name: string;
  task_name?: string;
  due_date?: string;
  hours_until_due?: number;
  action_required: string;
  metadata?: {
    blocker_reason?: string;
    related_party?: string;
    document_type?: string;
  };
}

interface DealWorkflowSummary {
  workflow_id: string;
  workflow_type: 'listing' | 'buying' | 'dual_agency';
  overall_progress: number;
  current_stage: string;
  days_in_current_stage: number;
  estimated_closing_date: string;
  days_until_closing: number;
  has_ai_coordinator: boolean;
  critical_alerts_count: number;
  total_alerts_count: number;
  next_milestone: {
    name: string;
    due_date: string;
    description: string;
  };
}

interface DealWorkflowPanelProps {
  className?: string;
  dealId: string;
  dealDetails?: any;
  refreshTrigger?: number;
  onWorkflowAlerts?: (alerts: DealWorkflowAlert[]) => void;
}

const DealWorkflowPanel: React.FC<DealWorkflowPanelProps> = ({
  className,
  dealId,
  dealDetails,
  refreshTrigger = 0,
  onWorkflowAlerts
}) => {
  const [loading, setLoading] = useState(true);
  const [workflowSummary, setWorkflowSummary] = useState<DealWorkflowSummary | null>(null);
  const [workflowStages, setWorkflowStages] = useState<DealWorkflowStage[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<DealWorkflowAlert[]>([]);
  const [error, setError] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Load workflow data
  const loadWorkflowData = async () => {
    if (!dealId) return;

    setLoading(true);
    setError('');

    try {
      console.log('🔄 [DEAL WORKFLOW] Loading workflow status for deal:', dealId);

      // Fetch deal workflow status
      const response = await axiosInstance.get(`/deal-workflow/deals/${dealId}/workflow/status`);
      
      if (response.data.success) {
        const { summary, stages, alerts } = response.data.data;
        
        setWorkflowSummary(summary);
        setWorkflowStages(stages);
        setActiveAlerts(alerts);
        
        // Send alerts to parent component if handler provided
        if (onWorkflowAlerts && alerts.length > 0) {
          onWorkflowAlerts(alerts);
        }
        
        console.log(`✅ [DEAL WORKFLOW] Loaded ${stages.length} stages with ${alerts.length} alerts`);
      }
    } catch (err: any) {
      console.error('❌ [DEAL WORKFLOW] Error loading workflow:', err);
      
      // Always load mock data for demo purposes
      console.log('📊 [DEAL WORKFLOW] Loading mock data for demo');
      loadMockWorkflowData();
    } finally {
      setLoading(false);
    }
  };

  // Mock data for development
  const loadMockWorkflowData = () => {
    const mockSummary: DealWorkflowSummary = {
      workflow_id: `WF-${dealId}`,
      workflow_type: 'buying',
      overall_progress: 35,
      current_stage: 'Due Diligence',
      days_in_current_stage: 3,
      estimated_closing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      days_until_closing: 30,
      has_ai_coordinator: dealDetails?.deal_owners?.some((owner: any) => owner.deal_owner_role === 'TransactionCoordinator') || false,
      critical_alerts_count: 2,
      total_alerts_count: 5,
      next_milestone: {
        name: 'Inspection Deadline',
        due_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        description: 'Complete property inspection and submit report'
      }
    };

    const mockStages: DealWorkflowStage[] = [
      {
        stage_id: '1',
        stage_name: 'Contract Execution',
        stage_order: 1,
        status: 'completed',
        completion_percentage: 100,
        tasks_completed: 8,
        total_tasks: 8,
        estimated_completion: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        is_current: false,
        alerts: []
      },
      {
        stage_id: '2',
        stage_name: 'Due Diligence',
        stage_order: 2,
        status: 'in_progress',
        completion_percentage: 60,
        tasks_completed: 6,
        total_tasks: 10,
        estimated_completion: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        is_current: true,
        alerts: [
          {
            alert_id: 'A1',
            alert_type: 'due_soon',
            priority: 'high',
            message: 'Property inspection due in 2 days',
            stage_name: 'Due Diligence',
            task_name: 'Schedule Property Inspection',
            due_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
            hours_until_due: 48,
            action_required: 'Schedule inspection with certified inspector'
          },
          {
            alert_id: 'A2',
            alert_type: 'attention_needed',
            priority: 'critical',
            message: 'Earnest money deposit pending',
            stage_name: 'Due Diligence',
            task_name: 'Submit Earnest Money',
            action_required: 'Wire earnest money to escrow account'
          }
        ]
      },
      {
        stage_id: '3',
        stage_name: 'Financing',
        stage_order: 3,
        status: 'pending',
        completion_percentage: 0,
        tasks_completed: 0,
        total_tasks: 12,
        estimated_completion: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
        is_current: false,
        alerts: []
      },
      {
        stage_id: '4',
        stage_name: 'Closing Preparation',
        stage_order: 4,
        status: 'pending',
        completion_percentage: 0,
        tasks_completed: 0,
        total_tasks: 15,
        estimated_completion: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
        is_current: false,
        alerts: []
      }
    ];

    const mockAlerts: DealWorkflowAlert[] = mockStages.flatMap(stage => stage.alerts);

    setWorkflowSummary(mockSummary);
    setWorkflowStages(mockStages);
    setActiveAlerts(mockAlerts);

    if (onWorkflowAlerts && mockAlerts.length > 0) {
      onWorkflowAlerts(mockAlerts);
    }
  };

  useEffect(() => {
    loadWorkflowData();
  }, [dealId, refreshTrigger]);

  // Trigger manual deal analysis
  const triggerDealAnalysis = async () => {
    if (!dealId || isAnalyzing) return;
    
    setIsAnalyzing(true);
    
    try {
      console.log('🔄 [DEAL WORKFLOW] Triggering manual analysis for deal:', dealId);
      
      // Call the refresh analysis endpoint
      const response = await axiosInstance.post(`/deal-analysis/deals/${dealId}/analyze`, {
        trigger: 'manual',
        analyzeAttachments: true,
        generateRecommendations: true
      });
      
      if (response.data.success) {
        toast.success('Deal analysis started. Refreshing workflow...', {
          icon: '🤖',
          duration: 3000
        });
        
        // Wait a moment for backend processing
        setTimeout(() => {
          loadWorkflowData();
        }, 2000);
        
      } else {
        toast.error('Failed to start analysis');
      }
      
    } catch (error: any) {
      console.error('❌ [DEAL WORKFLOW] Analysis trigger failed:', error);
      toast.error(error.response?.data?.message || 'Failed to trigger analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStageIcon = (stageName: string) => {
    switch (stageName.toLowerCase()) {
      case 'contract execution': return faFileContract;
      case 'due diligence': return faTasks;
      case 'financing': return faChartLine;
      case 'closing preparation': return faCalendarCheck;
      default: return faCheckCircle;
    }
  };

  if (loading) {
    return (
      <Card className={`${className} workflow-panel-loading`}>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading workflow status...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error && !workflowSummary) {
    return (
      <Card className={`${className} workflow-panel-error`}>
        <Card.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card 
      className={`${className} deal-workflow-panel`}
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
        border: '1px solid rgba(0, 212, 255, 0.1)',
        borderRadius: '16px',
        overflow: 'hidden'
      }}
    >
      <Card.Header 
        className="border-0 pb-0"
        style={{
          background: 'transparent',
          padding: '1.5rem 1.5rem 1rem'
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-start">
            <div 
              className="icon-wrapper me-3 flex-shrink-0"
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 212, 255, 0.2)',
                marginTop: '2px'
              }}
            >
              <FontAwesomeIcon 
                icon={faRocket} 
                className="text-white"
                style={{ fontSize: '20px' }}
              />
            </div>
            <div className="pt-1">
              <h5 className="mb-1 fw-bold" style={{ lineHeight: '1.2' }}>Deal Workflow Status</h5>
              <p className="text-muted small mb-0" style={{ lineHeight: '1.4' }}>
                {workflowSummary?.workflow_type ? 
                  workflowSummary.workflow_type.replace('_', ' ').charAt(0).toUpperCase() + 
                  workflowSummary.workflow_type.slice(1).replace('_', ' ') : 
                  'Real Estate'} Transaction
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={triggerDealAnalysis}
              disabled={isAnalyzing}
              style={{
                borderRadius: '20px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                border: '1px solid #00d4ff',
                background: isAnalyzing ? 'rgba(0, 212, 255, 0.1)' : 'transparent'
              }}
            >
              <FontAwesomeIcon 
                icon={faSync} 
                className={`me-1 ${isAnalyzing ? 'fa-spin' : ''}`}
              />
              {isAnalyzing ? 'Analyzing...' : 'Refresh Analysis'}
            </Button>
            {workflowSummary?.has_ai_coordinator && (
              <Badge 
              bg="success"
              style={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #10b981 100%)',
                border: 'none',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '20px'
              }}
            >
              <FontAwesomeIcon icon={faRobot} className="me-1" />
              AI Coordinator Active
            </Badge>
            )}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">Overall Progress</span>
            <span className="fw-bold">{workflowSummary?.overall_progress}%</span>
          </div>
          <ProgressBar 
            now={workflowSummary?.overall_progress || 0}
            style={{ 
              height: '8px',
              background: 'rgba(0, 212, 255, 0.1)'
            }}
          >
            <ProgressBar
              now={workflowSummary?.overall_progress || 0}
              style={{
                background: 'linear-gradient(90deg, #00d4ff 0%, #10b981 100%)'
              }}
            />
          </ProgressBar>
        </div>

        {/* Key Metrics */}
        <div className="d-flex justify-content-between text-center">
          <div>
            <p className="text-muted small mb-1">Days to Close</p>
            <h6 className="mb-0 fw-bold text-primary">{workflowSummary?.days_until_closing}</h6>
          </div>
          <div>
            <p className="text-muted small mb-1">Current Stage</p>
            <h6 className="mb-0 fw-bold">{workflowSummary?.current_stage}</h6>
          </div>
          <div>
            <p className="text-muted small mb-1">Active Alerts</p>
            <h6 className="mb-0 fw-bold text-warning">{workflowSummary?.total_alerts_count}</h6>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="pt-0">
        {/* Active Alerts */}
        {activeAlerts.length > 0 && (
          <div className="alerts-section mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
              Active Alerts ({activeAlerts.length})
            </h6>
            {activeAlerts.map(alert => (
              <Alert 
                key={alert.alert_id}
                variant={getPriorityColor(alert.priority)}
                className="mb-2 d-flex align-items-start"
                style={{ fontSize: '14px' }}
              >
                <div className="flex-grow-1">
                  <strong>{alert.message}</strong>
                  <div className="text-muted small mt-1">
                    {alert.action_required}
                    {alert.due_date && (
                      <span className="ms-2">
                        • Due in {alert.hours_until_due} hours
                      </span>
                    )}
                  </div>
                </div>
              </Alert>
            ))}
          </div>
        )}

        {/* Workflow Stages */}
        <div className="stages-section">
          <h6 className="mb-3">Transaction Stages</h6>
          {workflowStages.map((stage, index) => (
            <div 
              key={stage.stage_id}
              className={`stage-item mb-3 p-3 rounded ${stage.is_current ? 'current-stage' : ''}`}
              style={{
                background: stage.is_current ? 'rgba(0, 212, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                border: stage.is_current ? '1px solid rgba(0, 212, 255, 0.2)' : '1px solid transparent'
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon 
                    icon={getStageIcon(stage.stage_name)} 
                    className={`me-2 ${stage.status === 'completed' ? 'text-success' : 
                                      stage.status === 'in_progress' ? 'text-primary' : 'text-muted'}`}
                  />
                  <strong>{stage.stage_name}</strong>
                  {stage.is_current && (
                    <Badge bg="primary" className="ms-2" pill>Current</Badge>
                  )}
                </div>
                <span className="text-muted small">
                  {stage.tasks_completed}/{stage.total_tasks} tasks
                </span>
              </div>
              <ProgressBar 
                now={stage.completion_percentage}
                style={{ height: '6px' }}
                variant={stage.status === 'completed' ? 'success' : 'primary'}
              />
            </div>
          ))}
        </div>

        {/* Next Milestone */}
        {workflowSummary?.next_milestone && (
          <div 
            className="next-milestone mt-4 p-3 rounded"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}
          >
            <h6 className="mb-2 d-flex align-items-center">
              <FontAwesomeIcon icon={faClock} className="text-success me-2" />
              Next Milestone
            </h6>
            <strong>{workflowSummary.next_milestone.name}</strong>
            <p className="text-muted small mb-0 mt-1">
              {workflowSummary.next_milestone.description}
            </p>
            <small className="text-muted">
              Due: {new Date(workflowSummary.next_milestone.due_date).toLocaleDateString()}
            </small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DealWorkflowPanel;